import { useEffect } from 'react';
import API from '../API';
import { IntersectSign, parseIntersectSign } from '../components/UnityGame/intersectSign';

export function signTextureUrl(sign: IntersectSign): string {
  if (!sign.url) return '';
  // Separate Unity's CORS texture fetch from old no-CORS moderator previews.
  // Stable per revision: fixes previously cached responses without redownloading on every poll.
  const url = new URL(sign.url);
  url.searchParams.set('intersectTexture', `2-${sign.version}`);
  return url.toString();
}

/** All viewers fetch shared state, not just moderators with their tools open. */
export function useIntersectSign(accessToken: string | null | undefined, eventId: string, unityReady: boolean) {
  useEffect(() => {
    if (!accessToken || !unityReady) return;
    let disposed = false, pending = false, refreshAgain = false;
    let latestVersion = -1;
    const publish = (url: string) => window.sendMessageToUnity?.('BrowserBridge', 'ReceivePayload', JSON.stringify({
      eventType: 'IntersectSign', payload: JSON.stringify({ eventId, url }),
    }));
    publish(''); // New world/event starts with its own baked-in artwork, not another event's override.
    const apply = (sign: IntersectSign) => {
      if (disposed || sign.version < latestVersion) return;
      latestVersion = sign.version;
      publish(signTextureUrl(sign));
    };
    const refresh = async () => {
      if (disposed) return;
      if (pending) { refreshAgain = true; return; }
      pending = true;
      try {
        const response = await API.getIntersectSign(accessToken, { eventId });
        if (response.ok) {
          const sign = parseIntersectSign(await response.json());
          apply(sign);
        }
      } catch { /* Keep the current artwork on transient network errors. */ }
      finally { pending = false; if (refreshAgain && !disposed) { refreshAgain = false; void refresh(); } }
    };
    const changed = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail?.eventId !== eventId) return;
      // The uploader already has the committed snapshot; don't wait for another GET.
      try { if (detail.sign) apply(parseIntersectSign(detail.sign)); } catch { /* Refresh authoritative state below. */ }
      void refresh();
    };
    void refresh();
    const timer = window.setInterval(refresh, 10000);
    window.addEventListener('intersectSignUpdated', changed);
    return () => { disposed = true; clearInterval(timer); window.removeEventListener('intersectSignUpdated', changed); publish(''); };
  }, [accessToken, eventId, unityReady]);
}
